import { React, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/AddDevice.css';
import CustomButton from '../CustomElements/CustomButton';
import FormContainer from '../CustomElements/FormContainer';
import CustomContainer from '../CustomElements/CustomContainer';
import DateSelector from '../CustomElements/DateSelector';
import CustomTextarea from '../CustomElements/CustomTextarea';
import CustomTitle from '../CustomElements/CustomTitle';
import Selector from '../CustomElements/Selector';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import FilePicker from '../FileManager/FilePicker';
// Кастомные хуки
import { useButtonValidation } from '../../customHook/useButtonValidation';
import useFormDataChanged from '../../customHook/useFormDataChanged';
import useSuccessListener from '../../customHook/useSuccessListener';
// Утилиты
import { convertToDate, transformFieldsToMoscowStart } from '../../utils/convertToDate';
import { handleUniversalChange } from '../../utils/formUtils';
import { omitFields } from '../../utils/omitFields';
import { prepareObjectForSubmit } from '../../utils/prepareObjectForSubmit';
// Действия Redux
import { editStorageStatusFromServer } from '../../redux/actions/statuses.action';
import { load } from '../../redux/actions/loader.action';

// компонент изменения статуса хранение

function EditStorageStatus({ closeModal, currentStatus, currentDevice, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();
  // Получаем опции
  const optionsForSelect = useSelector((state) => state.options);

  // Состояния для кнопки и уведомления об успешном добавлении
  const [addSuccess, setAddSuccess] = useState(false);

  // отмена подтверждения
  const closeStatusEditModal = () => {
    setAddSuccess(false);
  };

  // Мемоизируем начальные данные формы
  const initialFormData = useMemo(
    () => ({
      id: currentStatus?.id,
      deviceId: currentStatus?.deviceId,
      fromStatusId: currentStatus?.fromStatusId || '',
      note: currentStatus?.note || '',
      isBroken: currentStatus?.broken,
      typeDevice: currentDevice?.typeId,
      date: convertToDate(currentStatus?.date) || '',
      storageId: currentStatus?.storageId || '',
      receiverId: currentStatus?.receiverId || '',
      attachment: currentStatus?.attachment || '',
      attachmentName: currentStatus?.attachmentName || '',
      acceptanceCert: currentStatus?.acceptanceCert || '',
      acceptanceCertName: currentStatus?.acceptanceCertName || '',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStatus]
  );

  // Состояние для формы
  const [formData, setFormData] = useState(initialFormData);

  //проверяем были ли изменения в форме (true-да, false-нет)
  const isFormEdit = useFormDataChanged(initialFormData, formData, true);

  // Обновляем formData при изменении currentStatus
  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  // Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, ['attachmentName', 'acceptanceCertName', 'attachment']);

  // Обработчик отправки формы
  const handlerSubmit = (event) => {
    event.preventDefault();
    //переводим в состояние загрузки
    dispatch(load());
    // сравниваем начальное и текущее состояние заменяем на null пустые строки для удаленных полей
    const formDataWithEmptyStrings = prepareObjectForSubmit(initialFormData, formData);
    // Удаляем поля, которые не нужно отправлять
    const formWithoutExcluded = omitFields(formDataWithEmptyStrings, [
      'id',
      'deviceId',
      'acceptanceCertName',
      'attachmentName',
    ]);
    // Преобразуем указанные поля в начало дня по Москве 
    const payload = transformFieldsToMoscowStart(formWithoutExcluded, ['date']);
    // Отправляем данные на сервер через Redux
    dispatch(
      editStorageStatusFromServer({
        payload,
        deviceId: formData?.deviceId,
        statusId: formData?.id,
        type: currentDevice?.typeId,
        path: 'storage',
      })
    );
  };

  //отслеживаем выполнение запроса
  useSuccessListener(setFormData, initialFormData, closeModal, closeStatusEditModal);

  // Открываем  модальное для подтверждения изменений
  const handlerOpenModal = () => {
    setAddSuccess(true);
  };

  return (
    <CustomContainer>
      <CustomTitle
        titleText={`${t?.deviceEditStatusTitle || 'ИЗМЕНЕНИЕ ДАННЫХ СТАТУСА'} ${currentDevice?.type} ${
          currentDevice?.id
        }`}
      />
      {addSuccess && (
        <ConfirmationPage
          confirmText={t?.statusChangeConfirmationText || 'Вы уверены, что хотите изменить данные статуса?'}
          confirmButtonText={t?.confirmButton || 'Изменить'}
          cancelButtonText={t?.cancelButton || 'Отменить'}
          onConfirm={handlerSubmit} // Функция, которая должна быть вызвана при подтверждении
          onCancel={closeStatusEditModal} // Функция для закрытия модального окна или отмены действия
        />
      )}
      {!addSuccess && (
        <FormContainer>
          <DateSelector
            column="left"
            width="100%"
            height="var(--sadr-font-size-60)"
            label={`${t?.labelReceiveDate} *` || 'Дата приемки *'}
            placeholder={t?.selectAcceptanceDate || 'Выберите дату приемки'}
            selectedDate={formData?.date}
            setSelectedDate={(date) => handleUniversalChange(formData, setFormData, null, 'date', date, 'date')}
            name="date"
            minDate={convertToDate(currentDevice?.lastActionDate)}
            maxDate={new Date()}
          />
          <Selector
            column="right"
            options={optionsForSelect.storages}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'storageId', selectedOption, 'select')
            }
            label={t?.labelStorageLocation || 'Место хранения *'}
            placeholder={t?.placeholderStorageLocation || 'Выберите место хранения'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={optionsForSelect?.storages?.find((option) => option.value === formData.storageId)}
            isMulti={false}
            name="storageId"
          />
          <Selector
            column="left"
            options={optionsForSelect.employees}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'receiverId', selectedOption, 'select')
            }
            label={t?.labelReceiverName || 'ФИО принявшего *'}
            placeholder={t?.placeholderReceiverName || 'Выберите ФИО принявшего'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={optionsForSelect?.employees?.find((option) => option.value === formData.receiverId)}
            isMulti={false}
            name="receiverId"
          />
          <Selector
            column="right"
            options={optionsForSelect?.condition}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'isBroken', selectedOption, 'select')
            }
            pl
            label={t?.labelCondition || 'Состояние *'}
            placeholder={t?.placeholderCondition || 'Не определено'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={
              formData.isBroken === undefined
                ? optionsForSelect.condition?.find(
                    (option) => option.label === (t?.placeholderCondition || 'Не определено')
                  )
                : formData.isBroken === ''
                ? null
                : optionsForSelect.condition?.find((option) => option.value === formData.isBroken)
            }
            isMulti={false}
            name="isBroken"
          />
          <FilePicker
            column="left"
            label={t?.labelAcceptanceCert || 'Акт приема-передачи *'}
            formData={formData}
            setFormData={setFormData}
            fieldName="acceptanceCertName"
            fieldIdName="acceptanceCert"
            rowSpan={2}
          />
          <CustomTextarea
            column="right"
            type="textArea"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            name="note"
            label={t?.labelComment || 'Комментарий *'}
            placeholder={t?.placeholderComment || 'Оставьте комментарий'}
            width="100%"
            height="var(--sadr-font-size-160)"
            value={formData.note}
          />
          <CustomButton
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            text={t?.buttonSaveChanges || 'Сохранить изменения'}
            disabled={isButtonDisabled || !isFormEdit}
            onClick={handlerOpenModal}
            isButton
          />
        </FormContainer>
      )}
    </CustomContainer>
  );
}

export default EditStorageStatus;
